import React from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Button,
  Avatar,
  Alert,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { BrandReceiptSettings } from '../../types/platformIntegration';

interface BrandReceiptHeaderFormProps {
  value: BrandReceiptSettings;
  onChange: (next: BrandReceiptSettings) => void;
  /** Verilmezse logo yükleme butonu yerine yalnızca URL alanı gösterilir. */
  onImageUpload?: (file: File) => Promise<string>;
  disabled?: boolean;
}

/**
 * Markaya özel fiş başlığı formu.
 *
 * Alan adları ve etiketleri quickSale ReceiptSettingsModal ile aynıdır; aynı
 * i18n anahtarları kullanılır ki ileride bir etiket değiştiğinde iki ekran
 * ayrışmasın. Yalnızca başlık ve alt bilgi alanlarını içerir — yazdırma
 * formatı gibi ekran geneli ayarlar markaya göre değişmez.
 */
const BrandReceiptHeaderForm: React.FC<BrandReceiptHeaderFormProps> = ({
  value,
  onChange,
  onImageUpload,
  disabled = false,
}) => {
  const { t } = useTranslation('salesSystem');
  const [logoUploading, setLogoUploading] = React.useState(false);
  const [logoError, setLogoError] = React.useState<string | null>(null);

  const headerType = value.headerType || 'text';

  const setField = <K extends keyof BrandReceiptSettings>(field: K, fieldValue: BrandReceiptSettings[K]) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onImageUpload) return;

    if (file.size > 5 * 1024 * 1024) {
      setLogoError(t('receiptSettingsModal.errors.logoTooLarge'));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setLogoError(t('receiptSettingsModal.errors.imageOnly'));
      return;
    }

    try {
      setLogoUploading(true);
      setLogoError(null);
      const logoUrl = await onImageUpload(file);
      setField('logoUrl', logoUrl);
    } catch (error: any) {
      setLogoError(t('receiptSettingsModal.errors.logoUploadFailed', { message: error?.message || '' }));
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('receiptSettingsModal.headerSettings.selectType')}
      </Typography>
      <RadioGroup
        row
        value={headerType}
        onChange={(e) => setField('headerType', e.target.value as 'text' | 'logo')}
      >
        <FormControlLabel
          value="text"
          control={<Radio disabled={disabled} />}
          label={t('receiptSettingsModal.headerSettings.textHeader')}
        />
        <FormControlLabel
          value="logo"
          control={<Radio disabled={disabled} />}
          label={t('receiptSettingsModal.headerSettings.logoHeader')}
        />
      </RadioGroup>

      {headerType !== 'logo' && (
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('receiptSettingsModal.companyInfo.companyName')}
                value={value.companyName || ''}
                onChange={(e) => setField('companyName', e.target.value)}
                fullWidth
                size="small"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('receiptSettingsModal.companyInfo.taxNumber')}
                value={value.taxNumber || ''}
                onChange={(e) => setField('taxNumber', e.target.value)}
                fullWidth
                size="small"
                disabled={disabled}
              />
            </Grid>
          </Grid>
          <TextField
            label={t('receiptSettingsModal.companyInfo.address')}
            value={value.companyAddress || ''}
            onChange={(e) => setField('companyAddress', e.target.value)}
            multiline
            rows={2}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            disabled={disabled}
          />
        </Box>
      )}

      {headerType === 'logo' && (
        <Box sx={{ mt: 1 }}>
          {logoError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {logoError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {value.logoUrl ? (
              <>
                <Avatar
                  src={value.logoUrl}
                  alt={t('receiptSettingsModal.logoSection.logoAlt')}
                  sx={{ width: 72, height: 72 }}
                  variant="rounded"
                />
                <Box>
                  <Typography variant="body2" color="success.main" gutterBottom>
                    {t('receiptSettingsModal.logoSection.logoUploaded')}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setField('logoUrl', '')}
                    disabled={disabled}
                  >
                    {t('receiptSettingsModal.logoSection.deleteLogo')}
                  </Button>
                </Box>
              </>
            ) : onImageUpload ? (
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="brand-receipt-logo-upload"
                  type="file"
                  onChange={handleLogoUpload}
                  disabled={logoUploading || disabled}
                />
                <label htmlFor="brand-receipt-logo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={logoUploading || disabled}
                  >
                    {logoUploading
                      ? t('receiptSettingsModal.common.uploading')
                      : t('receiptSettingsModal.logoSection.uploadLogo')}
                  </Button>
                </label>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                  {t('receiptSettingsModal.logoSection.logoHint')}
                </Typography>
              </Box>
            ) : (
              <TextField
                label={t('receiptSettingsModal.logoSection.title')}
                value={value.logoUrl || ''}
                onChange={(e) => setField('logoUrl', e.target.value)}
                fullWidth
                size="small"
                disabled={disabled}
              />
            )}
          </Box>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('receiptSettingsModal.logoSection.maxWidth')}
                type="number"
                value={value.logoMaxWidthPx ?? 384}
                onChange={(e) => setField('logoMaxWidthPx', parseInt(e.target.value, 10) || 384)}
                fullWidth
                size="small"
                helperText={t('receiptSettingsModal.logoSection.maxWidthHint')}
                inputProps={{ min: 48, max: 576 }}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('receiptSettingsModal.logoSection.maxHeight')}
                type="number"
                value={value.logoMaxHeightPx ?? 150}
                onChange={(e) => setField('logoMaxHeightPx', parseInt(e.target.value, 10) || 150)}
                fullWidth
                size="small"
                helperText={t('receiptSettingsModal.logoSection.maxHeightHint')}
                inputProps={{ min: 48, max: 300 }}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        {t('receiptSettingsModal.footerSettings.title')}
      </Typography>
      <TextField
        label={t('receiptSettingsModal.footerSettings.standardNote')}
        value={value.standardFooterNote || ''}
        onChange={(e) => setField('standardFooterNote', e.target.value)}
        multiline
        rows={2}
        fullWidth
        size="small"
        disabled={disabled}
      />
      <TextField
        label={t('receiptSettingsModal.footerSettings.thankYouMessage')}
        value={value.thankYouMessage || ''}
        onChange={(e) => setField('thankYouMessage', e.target.value)}
        fullWidth
        size="small"
        sx={{ mt: 2 }}
        disabled={disabled}
      />
    </Box>
  );
};

export default BrandReceiptHeaderForm;
